import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Badge, InputGroup, ListGroup } from 'react-bootstrap';
import { stockAPI, supplierAPI, materialsAPI } from '../utils/api';

const StockIntake = () => {
  const [barcode, setBarcode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [scannedMaterial, setScannedMaterial] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [latestTransactionId, setLatestTransactionId] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const [unitPrice, setUnitPrice] = useState('');

  // Supplier autocomplete states
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierId, setSupplierId] = useState(null);

  const barcodeInputRef = useRef(null);
  const focusDebounceRef = useRef(null);
  const supplierSearchDebounceRef = useRef(null);

  useEffect(() => {
    loadRecentTransactions();
    // Focus on barcode input on mount
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  // NOTE: Price auto-fill is now handled in handleBarcodeLookup, not here
  // useEffect(() => {
  //   if (scannedMaterial && scannedMaterial.cost) {
  //     setUnitPrice(scannedMaterial.cost.toString());
  //   }
  // }, [scannedMaterial]);

  const loadRecentTransactions = async () => {
    try {
      const res = await stockAPI.getTransactions({
        transactionType: 'intake',
        limit: 10
      });
      setRecentTransactions(res.data.data);
    } catch (err) {
      console.error('Error loading transactions:', err);
    }
  };

  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
    setError('');
    // Don't clear scannedMaterial immediately - let user see it while editing barcode
    // Clear latest transaction when starting a new scan
    setLatestTransactionId(null);
    // Don't clear unitPrice immediately either - let user see previous value
  };

  const handleBarcodeLookup = async (barcodeValue) => {
    if (!barcodeValue || barcodeValue.trim().length === 0) {
      setError('กรุณาระบุบาร์โค้ด');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await materialsAPI.getByBarcode(barcodeValue.trim());
      const material = res.data.data;

      // Display material info
      setScannedMaterial(material);

      // Auto-fill price from material cost
      if (material.cost) {
        setUnitPrice(material.cost.toString());
      } else {
        setUnitPrice('');
      }

      setSuccess(`พบสินค้า: ${material.name} - สต็อกปัจจุบัน ${material.quantityOnHand} ${material.unit}`);

      // Focus on quantity field for easy editing
      setTimeout(() => {
        // Could focus on quantity or price field here if desired
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'ไม่พบสินค้าที่มีบาร์โค้ดนี้');
      setScannedMaterial(null);
      setUnitPrice('');

      // Keep focus on barcode field for retry
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleBarcodeLookup(barcode);
    }
  };

  const handleSupplierSearch = async (searchText) => {
    // Clear previous debounce timeout
    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }

    // Hide dropdown if input is too short
    if (!searchText || searchText.trim().length < 2) {
      setSupplierSuggestions([]);
      setShowSupplierDropdown(false);
      return;
    }

    // Debounce the search request
    supplierSearchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await supplierAPI.search(searchText.trim());
        setSupplierSuggestions(res.data.data);
        setShowSupplierDropdown(res.data.data.length > 0);
      } catch (err) {
        console.error('Supplier search error:', err);
        setSupplierSuggestions([]);
        setShowSupplierDropdown(false);
      }
    }, 300);
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setReference(supplier.name);
    setSupplierId(supplier.id);
    setShowSupplierDropdown(false);
    setSupplierSuggestions([]);

    // Focus barcode input after selection
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
  };

  const handleReferenceChange = (e) => {
    const value = e.target.value;
    setReference(value);

    // Clear selected supplier if user manually edits
    if (selectedSupplier && value !== selectedSupplier.name) {
      setSelectedSupplier(null);
      setSupplierId(null);
    }

    // Trigger autocomplete search
    handleSupplierSearch(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!barcode || !quantity) {
      setError('กรุณาระบุบาร์โค้ดและจำนวน');
      return;
    }

    // NEW: Ensure material has been looked up first
    if (!scannedMaterial) {
      setError('กรุณาสแกนบาร์โค้ดก่อน (กด Enter หลังสแกน)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await stockAPI.recordIntake({
        barcode: barcode.trim(),
        quantity: parseFloat(quantity),
        reference: reference.trim(),
        note: note.trim(),
        supplierId: supplierId,
        unitPrice: unitPrice && parseFloat(unitPrice) > 0 ? parseFloat(unitPrice) : null
      });

      setSuccess(`บันทึกสำเร็จ: ${res.data.data.material.name} +${res.data.data.quantityAdded} ${res.data.data.material.unit}`);

      // Store the transaction ID for undo functionality
      setLatestTransactionId(res.data.data.transactionId);

      // Reset form (keep reference and note for batch scanning)
      setBarcode('');
      setQuantity('1');
      setScannedMaterial(null); // Clear preview after successful submit
      setUnitPrice(''); // Clear price for next scan
      // Keep supplier selection for batch scanning

      // Reload transactions
      loadRecentTransactions();

      // Focus back to barcode input (with small delay to ensure DOM updates)
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = async () => {
    if (!latestTransactionId) return;

    setUndoLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await stockAPI.undoTransaction(latestTransactionId);
      setSuccess(`ยกเลิกการบันทึกสำเร็จ: กลับสู่จำนวน ${res.data.data.restoredQuantity}`);
      setLatestTransactionId(null);
      setScannedMaterial(null);

      // Reload transactions
      loadRecentTransactions();

      // Focus back to barcode input
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการยกเลิก');
    } finally {
      setUndoLoading(false);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);

    // Clear previous debounce timeout
    if (focusDebounceRef.current) {
      clearTimeout(focusDebounceRef.current);
    }

    // Set new timeout to focus on barcode input after user stops clicking
    focusDebounceRef.current = setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 300);
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-upc-scan me-2"></i>
            รับสต็อกเข้าคลัง
          </h2>
          <p className="text-muted">สแกนบาร์โค้ดเพื่อบันทึกสินค้าเข้าคลัง</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">สแกนสินค้า</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
               
                <Form.Group className="mb-3">
                  <Form.Label>ร้านค้าที่รับมา (ซัพพลายเออร์)</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      placeholder="พิมพ์เพื่อค้นหาซัพพลายเออร์..."
                      value={reference}
                      onChange={handleReferenceChange}
                      disabled={loading}
                      autoComplete="off"
                    />
                    {showSupplierDropdown && supplierSuggestions.length > 0 && (
                      <ListGroup
                        style={{
                          position: 'absolute',
                          zIndex: 1000,
                          width: '100%',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          marginTop: '2px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                      >
                        {supplierSuggestions.map((supplier) => (
                          <ListGroup.Item
                            key={supplier.id}
                            action
                            onClick={() => handleSupplierSelect(supplier)}
                            style={{ cursor: 'pointer' }}
                          >
                            <strong>{supplier.code}</strong> - {supplier.name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหาซัพพลายเออร์
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>หมายเหตุ (ไม่บังคับ)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="หมายเหตุเพิ่มเติม..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>ราคาต่อหน่วย (ไม่บังคับ)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="ระบุราคาต่อหน่วย..."
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      disabled={loading || !scannedMaterial}
                    />
                    <InputGroup.Text>฿</InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    {scannedMaterial
                      ? `ราคาเริ่มต้นจากต้นทุนปัจจุบัน: ${scannedMaterial.cost?.toFixed(2) || '0.00'} ฿`
                      : 'สแกนบาร์โค้ดก่อนเพื่อดูราคาเริ่มต้น'}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>จำนวน</Form.Label>
                  <InputGroup>
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleQuantityChange(Math.max(1, parseFloat(quantity) - 1).toString())}
                      disabled={loading || parseFloat(quantity) <= 1}
                    >
                      <i className="bi bi-dash"></i>
                    </Button>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      placeholder="ระบุจำนวน..."
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={loading}
                      required
                      className="text-center"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleQuantityChange((parseFloat(quantity) + 1).toString())}
                      disabled={loading}
                    >
                      <i className="bi bi-plus"></i>
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>บาร์โค้ด</Form.Label>
                  <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="สแกนหรือพิมพ์บาร์โค้ด..."
                    value={barcode}
                    onChange={handleBarcodeChange}
                    onKeyPress={handleBarcodeKeyPress}
                    autoFocus
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    ใช้เครื่องสแกนบาร์โค้ดหรือพิมพ์ด้วยมือ (กด Enter เพื่อค้นหา)
                  </Form.Text>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading || !barcode || !quantity || undoLoading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        กำลังบันทึก...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        บันทึกสต็อก
                      </>
                    )}
                  </Button>

                  {latestTransactionId && (
                    <Button
                      variant="warning"
                      onClick={handleUndo}
                      disabled={undoLoading || loading}
                    >
                      {undoLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          กำลังยกเลิก...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-2"></i>
                          ยกเลิกการบันทึกล่าสุด
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </Form>

              {scannedMaterial && (
                <Alert variant="info" className="mt-3 mb-0">
                  <strong>📦 สินค้าที่สแกน:</strong><br/>
                  {scannedMaterial.code} - {scannedMaterial.name}<br/>
                  <strong>สต็อกปัจจุบัน:</strong> {scannedMaterial.quantityOnHand} {scannedMaterial.unit}<br/>
                  <small className="text-muted">ตรวจสอบข้อมูลและกด "บันทึกสต็อก" เพื่อยืนยัน</small>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">การบันทึกล่าสุด</h5>
            </Card.Header>
            <Card.Body className="p-0">
              {recentTransactions.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">ยังไม่มีการบันทึก</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>เวลา</th>
                      <th>สินค้า</th>
                      <th>จำนวน</th>
                      <th>ราคา/หน่วย</th>
                      <th>อ้างอิง</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((txn) => (
                      <tr key={txn.id}>
                        <td>
                          <small>
                            {new Date(txn.createdAt).toLocaleString('th-TH')}
                          </small>
                        </td>
                        <td>
                          <strong>{txn.material.name}</strong><br/>
                          <small className="text-muted">{txn.material.code}</small>
                        </td>
                        <td>
                          <Badge bg="success">
                            +{txn.quantity} {txn.material.unit}
                          </Badge>
                        </td>
                        <td>
                          {txn.unitPrice ? (
                            <small>{parseFloat(txn.unitPrice).toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</small>
                          ) : (
                            <small className="text-muted">-</small>
                          )}
                        </td>
                        <td>
                          <small>{txn.reference || '-'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default StockIntake;
